import { useState } from "react";
import type { VoucherApprovalBudgetLine } from "@/hooks/use-voucher-approval-context";
import type { VoucherAllocationRow } from "@/hooks/use-project-detail";
import { groupAllocationsByBudgetLine } from "@/lib/voucher-allocations";
import { calcBudgetLineDiff } from "@/lib/voucher-budget-diff";
import { formatNOK } from "@/lib/format";
import { PHASE_LABELS, type ProjectPhase } from "@/lib/offer-types";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface VoucherBudgetPanelProps {
  budgetLines: VoucherApprovalBudgetLine[];
  voucherAllocations: VoucherAllocationRow[];
  /** Beløpet på bilaget som er under godkjenning — inngår i differansen for valgt linje. */
  voucherAmount: number | null;
  /** `budget_line_id` valgt i godkjenningsflyten (evt. "__annet__"/"__ekstra__" — høylightes ikke). */
  selectedBudgetLineId: string | null;
}

function phaseLabel(phase: string | null): string | null {
  if (!phase) return null;
  return PHASE_LABELS[phase as ProjectPhase] ?? phase;
}

/**
 * VIB-1204: kompakt, ren lesevisning av prosjektets budsjettlinjer i
 * godkjenningsdialogen — fase, beskrivelse, budsjettbeløp, allerede ført
 * bilagskost («x bilag · y kr», samme grunnlag som budsjett-tabben) og
 * differanse for den valgte linja. Ingen mutasjon her; selve
 * godkjenningsflyten (valg/kommentar/knapper) eies av `VoucherApprovalModal`.
 */
export function VoucherBudgetPanel({
  budgetLines,
  voucherAllocations,
  voucherAmount,
  selectedBudgetLineId,
}: VoucherBudgetPanelProps) {
  const [showAll, setShowAll] = useState(false);
  const vouchersByBudgetLine = groupAllocationsByBudgetLine(voucherAllocations);

  // Kostnadslinjer (eksterne) er mest relevante for bilag — vis dem alltid.
  // Interne linjer bak "vis alle"-toggle (Erik 15.07: PM skal kunne velge
  // riktig linje, ikke bare bekrefte én — så alt er tilgjengelig, bare ikke
  // støyende som default).
  const externalLines = budgetLines.filter((bl) => bl.cost_type === "external");
  const internalLines = budgetLines.filter((bl) => bl.cost_type !== "external");
  const visibleLines = showAll ? [...externalLines, ...internalLines] : externalLines;

  if (budgetLines.length === 0) {
    return (
      <p className="text-xs text-muted-foreground py-2">
        Ingen budsjettlinjer funnet for prosjektet.
      </p>
    );
  }

  return (
    <div className="border rounded-md">
      <div className="max-h-56 overflow-y-auto divide-y">
        {visibleLines.map((bl) => {
          const allocated = vouchersByBudgetLine.get(bl.id);
          const isSelected = bl.id === selectedBudgetLineId;
          const diff = calcBudgetLineDiff(bl.amount, allocated?.amount ?? 0, isSelected ? voucherAmount : 0);
          return (
            <div
              key={bl.id}
              className={cn(
                "px-3 py-2 text-xs",
                isSelected && "bg-green-50/70",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <span className="text-muted-foreground">
                    {phaseLabel(bl.phase) ? `${phaseLabel(bl.phase)} – ` : ""}
                  </span>
                  <span className="font-medium">{bl.description ?? "Uten beskrivelse"}</span>
                </div>
                <span className="tabular-nums shrink-0">{formatNOK(bl.amount)}</span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5 text-[11px] text-muted-foreground">
                <span>
                  {allocated ? `${allocated.count} bilag · ${formatNOK(allocated.amount)}` : "Ingen bilag ført"}
                </span>
                {isSelected && (
                  <span
                    className={cn(
                      "font-medium tabular-nums",
                      diff >= 0 ? "text-green-700" : "text-red-700",
                    )}
                  >
                    Differanse: {formatNOK(diff)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {internalLines.length > 0 && (
        <div className="border-t px-2 py-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 text-[11px] text-muted-foreground"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? "Skjul interne linjer" : `Vis alle (${internalLines.length} interne linjer)`}
          </Button>
        </div>
      )}
    </div>
  );
}
